import { type FC, useEffect, useState } from "react"

import { getLoomIdFromUrl, getLoomUrlFromId } from "@forento/core/utilities/url"

import Button from "~/components/Button"
import InputField, { useRichTextArea } from "~/components/InputField"
import { useAlert } from "~/contexts/AlertContext"

import Attachments from "./Attachments"
import Links from "./Links"
import { ButtonsGroup, ButtonsRow } from "./styles"

type Props = {
	page: {
		id: number
		title: string
		text: string | null
		loomVideoId: string | null
		attachments: { id: number; fileType: string; label: string; url: string }[]
		links: { id: number; label: string; url: string }[]
	}
	isSaving: boolean
	isDeleting: boolean
	onClose(): void
	onSavePage(data: { title: string; text: string | null; loomVideoId: string }): void
	onDeletePage(): void
}

const LoomContent: FC<Props> = ({ page, isSaving, isDeleting, onClose, onSavePage, onDeletePage }) => {
	const alert = useAlert()

	const [title, setTitle] = useState(page.title)
	const text = useRichTextArea({ label: "Text", initialValue: page.text, extendedFormatting: true })
	const [loomVideoUrl, setLoomVideoUrl] = useState("")

	useEffect(() => {
		setTitle(page.title)
		setLoomVideoUrl(getLoomUrlFromId(page.loomVideoId))
	}, [page])

	async function handleSave() {
		const loomVideoId = getLoomIdFromUrl(loomVideoUrl)
		if (loomVideoId === null) {
			await alert.show(
				"Invalid Loom URL",
				"You have provided an invalid Loom URL. Please copy the URL from the address bar when watching a Loom video.",
			)
			return
		}

		onSavePage({ title, text: await text.exportEditorState(), loomVideoId })
	}

	return (
		<>
			<InputField label="Title" value={title} onChange={setTitle} />
			{text.element}
			<Attachments pageId={page.id} attachments={page.attachments} />
			<Links pageId={page.id} links={page.links} />
			<InputField label="Loom URL" value={loomVideoUrl} onChange={setLoomVideoUrl} />
			<ButtonsRow>
				<ButtonsGroup>
					<Button variant="secondary" onClick={onClose} isDisabled={isSaving || isDeleting}>
						Cancel
					</Button>
					<Button
						variant="primary"
						isDisabled={title.trim().length === 0 || isDeleting}
						isLoading={isSaving}
						onClick={handleSave}
					>
						Save
					</Button>
				</ButtonsGroup>
				<Button variant="primary-danger" onClick={onDeletePage} isDisabled={isSaving} isLoading={isDeleting}>
					Delete
				</Button>
			</ButtonsRow>
		</>
	)
}

export default LoomContent
